import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { ListEditor } from './Routine';
import { formatMoney, netPnl, usd } from './pnl';
import { GradeBadge, averageGrade, isTopGrade } from './grades';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { confirmDialog } from './confirmDialog';

const STATUSES = [
  { id: 'active', label: 'Active' },
  { id: 'testing', label: 'Testing' },
  { id: 'retired', label: 'Retired' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));
const NEW_TAG = '__new';

function pnlClass(n) {
  return n > 0 ? 'gain' : n < 0 ? 'loss' : '';
}

function hasTag(trade, tag) {
  const key = tag.toLowerCase();
  return (trade.tags || []).some((t) => t.toLowerCase() === key);
}

// The play a trade belongs to: the first play whose setup tag it carries (active plays first).
export function playForTrade(trade, plays) {
  if (!plays || !plays.length) return null;
  const order = { active: 0, testing: 1, retired: 2 };
  return [...plays].sort((a, b) => order[a.status] - order[b.status]).find((p) => hasTag(trade, p.tag)) || null;
}

// Results for one play, from the trades tagged with its setup tag.
export function playStats(play, trades) {
  const list = trades.filter((t) => hasTag(t, play.tag)).sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = list.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const grossWin = wins.reduce((a, b) => a + b, 0);
  const grossLoss = -losses.reduce((a, b) => a + b, 0);
  let running = 0;
  const series = [0, ...nets.map((n) => (running += n))];
  const summarize = (group) => {
    const n = group.map(netPnl);
    return { count: group.length, net: n.reduce((a, b) => a + b, 0), winRate: group.length ? (n.filter((x) => x > 0).length / group.length) * 100 : null };
  };
  const rules = play.entryRules || [];
  const reviewed = rules.length ? list.filter((t) => t.rulesReviewed) : [];
  const followed = reviewed.filter((t) => rules.every((r) => (t.rulesMet || []).includes(r)));
  const broke = reviewed.filter((t) => !followed.includes(t));
  return {
    list,
    count: list.length,
    net: nets.reduce((a, b) => a + b, 0),
    winRate: list.length ? (wins.length / list.length) * 100 : null,
    avg: list.length ? nets.reduce((a, b) => a + b, 0) / list.length : null,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : null,
    best: list.length ? Math.max(...nets) : null,
    worst: list.length ? Math.min(...nets) : null,
    lastDate: list.length ? list[list.length - 1].date : null,
    series,
    reviewed: reviewed.length,
    followed: summarize(followed),
    broke: summarize(broke),
    avgGrade: averageGrade(list.map((t) => t.grade)),
    graded: list.filter((t) => t.grade).length,
    topTrades: list.filter((t) => isTopGrade(t.grade)).reverse(),
  };
}

function formatPf(pf) {
  return pf === null ? '—' : pf === Infinity ? '∞' : pf.toFixed(2);
}

function MiniLine({ values, height = 36 }) {
  if (values.length < 3) return <div className="play-line-empty" style={{ height }} />;
  const w = 160;
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const range = max - min || 1;
  const x = (i) => (i / (values.length - 1)) * w;
  const y = (v) => height - 3 - ((v - min) / range) * (height - 6);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="play-line" style={{ height }} aria-hidden="true">
      <line x1="0" x2={w} y1={y(0)} y2={y(0)} className="tile-zero" />
      <path d={d} className={`tile-spark-line ${values[values.length - 1] >= 0 ? 'up' : 'down'}`} />
    </svg>
  );
}

// The play's fields. In the "+ New play" window it's a pop-up; on a play's own page
// (`inline`) the same fields sit right on the page, with Save changes when something changed.
function PlayEditor({ play, library, onSaved, onDeleted, onClose, onSessionExpired, inline = false, onDirtyChange }) {
  const { t } = useI18n();
  const isNew = !play._id;
  const setupTags = library.filter((x) => x.kind === 'setup').map((x) => x.name);
  const initial = useMemo(() => ({
    name: play.name || '',
    tag: play.tag || (play.name && setupTags.includes(play.name) ? play.name : NEW_TAG),
    status: play.status || 'active',
    description: play.description || '',
    entryRules: play.entryRules || [],
    exitRules: play.exitRules || [],
    risk: play.risk || '',
    chartUrl: play.chartUrl || '',
  }), [play]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState(initial);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const [savedNote, setSavedNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialog = useRef(null);
  const tagOptions = form.tag !== NEW_TAG && !setupTags.includes(form.tag) ? [form.tag, ...setupTags] : setupTags;

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (inline) return undefined;
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') closeRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inline]);

  function set(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { ...form, tag: form.tag === NEW_TAG ? form.name : form.tag };
      const saved = await api(isNew ? '/playbook' : `/playbook/${play._id}`, { method: isNew ? 'POST' : 'PATCH', body });
      onSaved(saved);
      if (inline) {
        setSaving(false);
        setSavedNote(true);
        setTimeout(() => setSavedNote(false), 2500);
      }
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    try {
      await api(`/playbook/${play._id}`, { method: 'DELETE' });
      onDeleted(play._id);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  const body = (
    <>
        <div className="fields">
          <label>
            {t('Name')}
            <input name="name" value={form.name} onChange={set} maxLength={80} placeholder={t('e.g. Opening range breakout')} required />
          </label>
          <label>
            {t('Setup tag')}
            <select name="tag" value={form.tag} onChange={set}>
              {tagOptions.map((x) => <option key={x} value={x}>{x}</option>)}
              <option value={NEW_TAG}>{form.name.trim() ? t('+ New tag: {name}', { name: form.name.trim() }) : t('+ New tag (uses the play name)')}</option>
            </select>
          </label>
          <label>
            {t('Status')}
            <select name="status" value={form.status} onChange={set}>
              {STATUSES.map((s) => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
            </select>
          </label>
        </div>
        <p className="field-hint play-tag-hint">{t("Trades tagged with this setup count toward the play's stats.")}</p>

        <label>
          {t('When to use it')}
          <textarea name="description" rows="3" value={form.description} onChange={set} maxLength={3000}
            placeholder={t('Market conditions, time of day, what the chart should look like…')} />
        </label>

        <ListEditor label={t('Entry rules')} items={form.entryRules} numbered onChange={(entryRules) => setForm({ ...form, entryRules })}
          placeholder={t('Add an entry rule, e.g. Wait for a 5-minute close above the range')} />
        <ListEditor label={t('Exit, stop & target rules')} items={form.exitRules} onChange={(exitRules) => setForm({ ...form, exitRules })}
          placeholder={t('Add an exit rule, e.g. Stop below the range low, target 2R')} />

        <div className="fields">
          <label>
            {t('Risk notes')}
            <input name="risk" value={form.risk} onChange={set} maxLength={1000} placeholder={t('e.g. 1 contract, max $250 risk')} />
          </label>
          <label>
            {t('Example chart link (optional)')}
            <input name="chartUrl" type="url" value={form.chartUrl} onChange={set} maxLength={500} placeholder="https://www.tradingview.com/x/…" />
          </label>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={saving || !form.name.trim() || (inline && !dirty)}>{saving ? t('Saving…') : isNew ? t('Create play') : t('Save changes')}</button>
          {inline ? (
            <>
              {dirty && <button type="button" className="secondary" onClick={() => setForm(initial)} disabled={saving}>{t('Undo changes')}</button>}
              {savedNote && !dirty && <span className="save-status success">{t('Saved')}</span>}
            </>
          ) : (
            <button type="button" className="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</button>
          )}
          {!isNew && (
            <div className="modal-delete">
              {confirmDelete ? (
                <>
                  <span>{t('Delete this play? Your trades keep their tags.')}</span>
                  <button type="button" className="danger small" onClick={remove} disabled={saving}>{t('Yes, delete')}</button>
                  <button type="button" className="link-button" onClick={() => setConfirmDelete(false)}>{t('No')}</button>
                </>
              ) : (
                <button type="button" className="link-button danger-link" onClick={() => setConfirmDelete(true)}>{t('Delete play')}</button>
              )}
            </div>
          )}
        </div>
    </>
  );

  if (inline) {
    return <form className="play-section play-edit-inline" onSubmit={save} aria-label={t('Edit play')}>{body}</form>;
  }
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form ref={dialog} tabIndex={-1} className="modal modal-wide" onSubmit={save} role="dialog" aria-modal="true" aria-labelledby="play-title">
        <div className="modal-header">
          <h2 id="play-title">{isNew ? t('New play') : t('Edit play')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        {body}
      </form>
    </div>
  );
}

function PlayCard({ play, stats, onOpen }) {
  const { t, tr } = useI18n();
  const strong = (x) => <strong>{x}</strong>;
  return (
    <li>
      <button type="button" className={`play-card status-${play.status}`} onClick={onOpen}>
        <span className="play-card-head">
          <span className="play-card-name">{play.name}</span>
          <span className="play-card-badges">
            {stats.avgGrade && <span title={t('Average grade')}><GradeBadge grade={stats.avgGrade} /></span>}
            <span className={`play-status status-${play.status}`}>{t(STATUS_LABEL[play.status])}</span>
          </span>
        </span>
        {play.description && <span className="play-card-desc">{play.description}</span>}
        <MiniLine values={stats.series} />
        <span className="play-card-stats">
          <span><strong className={pnlClass(stats.net)}>{stats.count ? formatMoney(stats.net) : '—'}</strong> {t('net')}</span>
          <span>{tr(stats.count === 1 ? '<0>{n}</0> trade' : '<0>{n}</0> trades', [strong], { n: stats.count })}</span>
          <span>{tr('<0>{rate}</0> win rate', [strong], { rate: stats.winRate === null ? '—' : `${Math.round(stats.winRate)}%` })}</span>
        </span>
        <span className="play-card-rules">{t(play.entryRules.length === 1 ? '{n} entry rule' : '{n} entry rules', { n: play.entryRules.length })}</span>
      </button>
    </li>
  );
}

function StatBox({ label, value, className = '' }) {
  return (
    <div className="play-stat">
      <span className="stat-label">{label}</span>
      <span className={`play-stat-value ${className}`}>{value}</span>
    </div>
  );
}

function PlayDetail({ play, stats, onBack, onEditTrade, editor }) {
  const { t, tr } = useI18n();
  const rulesCompare = stats.reviewed > 0;
  const recent = [...stats.list].reverse().slice(0, 12);
  return (
    <div className="play-detail">
      <div className="play-detail-bar">
        <button type="button" className="link-button" onClick={onBack}>&larr; {t('All plays')}</button>
      </div>

      <div className="play-detail-head">
        <div>
          <h2>{play.name}</h2>
          <span className="play-detail-meta">
            <span className={`play-status status-${play.status}`}>{t(STATUS_LABEL[play.status])}</span>
            {tr('Tag: <0>{tag}</0>', [(x) => <strong>{x}</strong>], { tag: play.tag })}
            {stats.lastDate && <> &middot; {t('Last traded {date}', { date: new Date(stats.lastDate).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' }) })}</>}
          </span>
        </div>
        {play.chartUrl && (
          <a className="secondary small play-chart-link" href={play.chartUrl} target="_blank" rel="noopener noreferrer">{t('Example chart')} &rarr;</a>
        )}
      </div>
      {editor}

      <div className="play-stats">
        <StatBox label={t('Net P&L')} value={stats.count ? formatMoney(stats.net) : '—'} className={pnlClass(stats.net)} />
        <StatBox label={t('Trades')} value={stats.count} />
        <StatBox label={t('Win rate')} value={stats.winRate === null ? '—' : `${Math.round(stats.winRate)}%`} />
        <StatBox label={t('Avg per trade')} value={stats.avg === null ? '—' : formatMoney(stats.avg)} className={pnlClass(stats.avg || 0)} />
        <StatBox label={t('Profit factor')} value={formatPf(stats.profitFactor)} className={stats.profitFactor === null ? '' : stats.profitFactor >= 1 ? 'gain' : 'loss'} />
        <StatBox label={t('Best / worst')} value={stats.count ? `${usd.format(stats.best)} / ${usd.format(stats.worst)}` : '—'} />
        <StatBox label={t('Average grade')} value={stats.avgGrade ? <GradeBadge grade={stats.avgGrade} /> : '—'} />
      </div>
      {stats.count > 2 && (
        <div className="play-equity">
          <span className="stat-label">{t('Running P&L on this play')}</span>
          <MiniLine values={stats.series} height={90} />
        </div>
      )}

      <section className="play-section">
        <h3>{t('Following the rules vs. not')}</h3>
        {rulesCompare ? (
          <div className="play-compare">
            {[['Followed every entry rule', stats.followed, 'good'], ['Skipped at least one rule', stats.broke, 'bad']].map(([label, g, tone]) => (
              <div key={label} className={`play-compare-box ${tone}`}>
                <span className="stat-label">{t(label)}</span>
                <strong className={pnlClass(g.net)}>{g.count ? formatMoney(g.net) : '—'}</strong>
                <span>
                  {t(g.count === 1 ? '{n} trade' : '{n} trades', { n: g.count })}
                  {g.winRate !== null && ` · ${t('{rate}% win rate', { rate: Math.round(g.winRate) })}`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="sidebar-muted">
            {play.entryRules.length
              ? t('Open a trade tagged with this setup and tick which entry rules it followed. This section then compares your results when you follow the rules with when you don’t.')
              : t('Add entry rules to this play, then tick them on your trades to see whether following them pays off.')}
          </p>
        )}
      </section>

      <section className="play-section">
        <h3>{t('A-grade examples')}</h3>
        {stats.topTrades.length ? (
          <ul className="best-trades">
            {stats.topTrades.slice(0, 6).map((trade) => (
              <li key={trade._id}>
                <button type="button" className="best-trade" onClick={() => onEditTrade(trade)}>
                  <span className="best-trade-top">
                    <GradeBadge grade={trade.grade} />
                    <span>{new Date(trade.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })} &middot; {trade.symbol} &middot; {t(trade.side || 'Long')}</span>
                    <span className={`best-trade-pnl ${pnlClass(netPnl(trade))}`}>{formatMoney(netPnl(trade))}</span>
                  </span>
                  {trade.notes && <span className="best-trade-notes">{trade.notes}</span>}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="sidebar-muted">{t('Your A+ and A trades with this setup show up here, so you can see what this play looks like at its best.')}</p>
        )}
      </section>

      <section className="play-section">
        <h3>{t('Recent trades')}</h3>
        {recent.length ? (
          <ul className="day-trades">
            {recent.map((trade) => {
              const net = netPnl(trade);
              const rules = play.entryRules;
              const allMet = trade.rulesReviewed && rules.length && rules.every((r) => (trade.rulesMet || []).includes(r));
              return (
                <li key={trade._id}>
                  <button type="button" className="day-trade-row play-trade-row" onClick={() => onEditTrade(trade)} title={t('Open this trade')}>
                    <span className="day-trade-time">{new Date(trade.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}</span>
                    <span className="day-trade-symbol">{trade.symbol}</span>
                    <span className="day-trade-side">{t(trade.side || 'Long')} &times; {trade.size || 1}</span>
                    <span className="play-trade-rules">
                      <GradeBadge grade={trade.grade} />{' '}
                      {!rules.length ? '' : !trade.rulesReviewed ? <span className="add-tag-hint">{t('Check rules')}</span>
                        : allMet ? <span className="gain">{t('All rules')} ✓</span>
                          : <span className="loss">{t('{met}/{total} rules', { met: rules.filter((r) => (trade.rulesMet || []).includes(r)).length, total: rules.length })}</span>}
                    </span>
                    <span className={`day-trade-pnl ${pnlClass(net)}`}>{formatMoney(net)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : <p className="sidebar-muted">{tr('No trades tagged <0>{tag}</0> yet in this view.', [(x) => <strong>{x}</strong>], { tag: play.tag })}</p>}
      </section>
    </div>
  );
}

// The trader's A+ and A trades, with ways to turn them into plays.
function BestTrades({ trades, plays, library, onOpenPlay, onAddToPlay, onNewPlay, onEditTrade }) {
  const { t } = useI18n();
  const setupNames = library.filter((x) => x.kind === 'setup').map((x) => x.name.toLowerCase());
  const setupOf = (trade) => (trade.tags || []).find((name) => setupNames.includes(name.toLowerCase()));
  const best = trades.filter((x) => isTopGrade(x.grade)).sort((a, b) => new Date(b.date) - new Date(a.date));
  // Setups that keep producing A trades but have no play yet.
  const counts = {};
  for (const trade of best) {
    const setup = setupOf(trade);
    if (setup && !plays.some((p) => p.tag.toLowerCase() === setup.toLowerCase())) counts[setup] = (counts[setup] || 0) + 1;
  }
  const suggestions = Object.entries(counts).filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]);

  return (
    <section className="play-section best-trades-panel">
      <h3>{t('Your best trades')}</h3>
      {best.length === 0 ? (
        <p className="sidebar-muted">
          {t('Grade your trades A+ to B- in the trade window. Your A+ and A trades show up here, so you can turn what works into plays and build your playbook from your best trading.')}
        </p>
      ) : (
        <>
          <p className="field-hint">{t('Your A+ and A trades. Turn the patterns behind them into plays.')}</p>
          {suggestions.length > 0 && (
            <div className="best-suggestions">
              {suggestions.map(([setup, n]) => (
                <button key={setup} type="button" className="secondary small" onClick={() => onNewPlay({ name: setup, tag: setup })}>
                  {t('+ Play for {setup} ({n} A trades)', { setup, n })}
                </button>
              ))}
            </div>
          )}
          <ul className="best-trades">
            {best.slice(0, 10).map((trade) => {
              const play = playForTrade(trade, plays);
              const setup = setupOf(trade);
              return (
                <li key={trade._id} className="best-trade-item">
                  <button type="button" className="best-trade" onClick={() => onEditTrade(trade)} title={t('Open this trade')}>
                    <span className="best-trade-top">
                      <GradeBadge grade={trade.grade} />
                      <span>{new Date(trade.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })} &middot; {trade.symbol} &middot; {t(trade.side || 'Long')}{setup ? ` · ${setup}` : ''}</span>
                      <span className={`best-trade-pnl ${pnlClass(netPnl(trade))}`}>{formatMoney(netPnl(trade))}</span>
                    </span>
                    {trade.notes && <span className="best-trade-notes">{trade.notes}</span>}
                  </button>
                  <div className="best-trade-actions">
                    {play ? (
                      <button type="button" className="link-button" onClick={() => onOpenPlay(play._id)}>{t('In play: {name}', { name: play.name })} &rarr;</button>
                    ) : (
                      <>
                        {plays.length > 0 && (
                          <select value="" onChange={(e) => e.target.value && onAddToPlay(trade, plays.find((p) => p._id === e.target.value))}
                            aria-label={t('Add this trade to a play')}>
                            <option value="">{t('Add to a play…')}</option>
                            {plays.filter((p) => p.status !== 'retired').map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                          </select>
                        )}
                        <button type="button" className="secondary small"
                          onClick={() => onNewPlay({ name: setup || '', tag: setup, description: trade.notes || '', fromTrade: trade })}>
                          {t('New play from this trade')}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

function Playbook({ trades, plays, setPlays, library, onLibraryReload, accountName, onEditTrade, onTradeUpdated, onSessionExpired }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState('current');
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [unsaved, setUnsaved] = useState(false);

  const statsById = useMemo(() => Object.fromEntries(plays.map((p) => [p._id, playStats(p, trades)])), [plays, trades]);
  const shown = plays.filter((p) => (filter === 'all' ? true : filter === 'retired' ? p.status === 'retired' : p.status !== 'retired'));
  const open = plays.find((p) => p._id === openId);
  const setupsWithoutPlay = library.filter((x) => x.kind === 'setup' && !plays.some((p) => p.tag.toLowerCase() === x.name.toLowerCase()));

  // Adds a play's setup tag to a trade, so it counts toward that play.
  async function addTradeToPlay(trade, play) {
    if (!play || hasTag(trade, play.tag)) return;
    setError('');
    try {
      const updated = await api(`/trades/${trade._id}`, { method: 'PATCH', body: { tags: [...(trade.tags || []), play.tag] } });
      onTradeUpdated(updated);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    }
  }

  async function leavePlay() {
    if (unsaved && !(await confirmDialog({ title: t('Leave without saving?'), message: t('Your changes to this play haven’t been saved yet. If you leave now, they’ll be lost.'),
      confirmLabel: t('Leave without saving'), cancelLabel: t('Keep editing'), danger: true }))) return;
    setUnsaved(false);
    setOpenId(null);
  }

  async function handleSaved(saved) {
    const fromTrade = editing && editing.fromTrade;
    setPlays((list) => (list.some((p) => p._id === saved._id) ? list.map((p) => (p._id === saved._id ? saved : p)) : [...list, saved]));
    setEditing(null);
    onLibraryReload();
    if (fromTrade) await addTradeToPlay(fromTrade, saved);
  }

  function handleDeleted(id) {
    setPlays((list) => list.filter((p) => p._id !== id));
    setEditing(null);
    setOpenId(null);
  }

  async function createFromTags() {
    setCreating(true);
    setError('');
    try {
      const created = [];
      for (const tag of setupsWithoutPlay) {
        created.push(await api('/playbook', { method: 'POST', body: { name: tag.name, tag: tag.name } }));
      }
      setPlays((list) => [...list, ...created]);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="playbook-page">
      <div className="routine-header">
        <div>
          <p className="eyebrow">{t('Playbook')}</p>
          <h1 className="routine-title">{open ? t('Your play') : t('Your setups')}</h1>
          <p className="subtitle">{t('The setups you trade, the rules that make them work, and how each one performs')} &bull; {accountName}</p>
        </div>
        {!open && <button type="button" className="page-action" onClick={() => setEditing({})}>{t('+ New play')}</button>}
      </div>

      {error && <p className="error">{error}</p>}

      {open ? (
        <PlayDetail play={open} stats={statsById[open._id]} onBack={leavePlay} onEditTrade={onEditTrade}
          editor={(
            <PlayEditor key={open._id} inline play={open} library={library} onSaved={handleSaved}
              onDeleted={(id) => { setUnsaved(false); handleDeleted(id); }} onDirtyChange={setUnsaved} onSessionExpired={onSessionExpired} />
          )} />
      ) : plays.length === 0 ? (
        <div className="unlock-card playbook-empty">
          <span className="unlock-label">{t('Build your playbook')}</span>
          <h2 className="unlock-title">{t('Write down the setups you trade')}</h2>
          <p className="unlock-why">
            {t('A play is one setup with clear rules: when to take it, how to enter, where your stop and target go. Tag trades with the setup and each play shows its own win rate and P&L, plus whether following your rules pays off.')}
          </p>
          <div className="unlock-actions">
            {setupsWithoutPlay.length > 0 && (
              <button type="button" className="small" onClick={createFromTags} disabled={creating}>
                {creating ? t('Creating…') : t('Start from my setup tags ({tags})', { tags: setupsWithoutPlay.map((x) => x.name).join(', ') })}
              </button>
            )}
            <button type="button" className="secondary small" onClick={() => setEditing({})}>{t('+ New play')}</button>
          </div>
        </div>
      ) : null}
      {!open && plays.length === 0 && (
        <BestTrades trades={trades} plays={plays} library={library} onOpenPlay={setOpenId} onAddToPlay={addTradeToPlay}
          onNewPlay={setEditing} onEditTrade={onEditTrade} />
      )}
      {open || plays.length === 0 ? null : (
        <>
          <div className="inbox-filter playbook-filter" role="group" aria-label={t('Show plays')}>
            {[['current', 'Active & testing'], ['retired', 'Retired'], ['all', 'All']].map(([id, label]) => (
              <button key={id} type="button" className={`secondary small${filter === id ? ' active' : ''}`} onClick={() => setFilter(id)}>{t(label)}</button>
            ))}
          </div>
          {shown.length ? (
            <ul className="play-grid">
              {shown.map((p) => <PlayCard key={p._id} play={p} stats={statsById[p._id]} onOpen={() => setOpenId(p._id)} />)}
            </ul>
          ) : <p className="empty">{t('No plays here yet.')}</p>}
          <BestTrades trades={trades} plays={plays} library={library} onOpenPlay={setOpenId} onAddToPlay={addTradeToPlay}
            onNewPlay={setEditing} onEditTrade={onEditTrade} />
          {setupsWithoutPlay.length > 0 && (
            <p className="field-hint playbook-more">
              {t('Setup tags without a play: {tags}.', { tags: setupsWithoutPlay.map((x) => x.name).join(', ') })}{' '}
              <button type="button" className="link-button" onClick={createFromTags} disabled={creating}>{t('Create plays for them')}</button>
            </p>
          )}
        </>
      )}

      {editing && (
        <PlayEditor play={editing} library={library} onSaved={handleSaved} onDeleted={handleDeleted} onClose={() => setEditing(null)}
          onSessionExpired={onSessionExpired} />
      )}
    </div>
  );
}

export default Playbook;
